export class RenderStringError extends Error {
  invalidPlaceholders: string[]
  invalidFilters: string[]

  constructor({
    invalidPlaceholders,
    invalidFilters
  }: {
    invalidPlaceholders: string[]
    invalidFilters: string[]
  }) {
    let message = ''
    if (invalidPlaceholders.length) {
      message += `The following placeholders are invalid: ${invalidPlaceholders.join(', ')}. `
    }
    if (invalidFilters.length) {
      message += `The following filters are invalid: ${invalidFilters.join(', ')}. `
    }
    message = message.trim()
    super(message)
    this.name = 'RenderStringError'
    this.invalidPlaceholders = invalidPlaceholders
    this.invalidFilters = invalidFilters
  }
}

// matches template strings and captures the context key
// allows whitespace between things
// ex: {{ myKey | lower }}
const reTemplate = /{{\s*([a-z]+)\s*(?:\|\s*([a-z\s|]+)\s*)?}}/gi

const filters: Record<string, (value: string) => string> = {
  lower: (value: unknown) => String(value).toLocaleLowerCase(),
  upper: (value: unknown) => String(value).toLocaleUpperCase()
}

export type RenderStringContext = Record<string, string>

export interface RenderStringOptions {
  throwOnError?: boolean
}

/**
 * A simple version of the nunjucks renderString method to take a template
 * string and context object and perform interpolation of any placeholder values.
 *
 * Supports simple filters
 * - lower : lowercase the value
 * - upper : uppercase the value
 *
 * If a key or filter is unknown, it will be ignored without errors unless the
 * `throwOnError` option is set.
 *
 * @example
 * renderString('Hello {{ name | upper }}!', { name: 'Bob' }) => 'Hello, BOB!'
 */
export function renderString(
  value: string,
  context: RenderStringContext,
  options: RenderStringOptions = {}
): string {
  if (value.indexOf('{{') === -1) return value // short-circuit
  const invalidFields: string[] = []
  const invalidFilters: string[] = []
  const newValue = value.replace(reTemplate, (fullMatch, contextKey, filterKeys) => {
    contextKey = contextKey.trim()
    let substitution = context[contextKey] || ''
    if (options.throwOnError && !substitution && !invalidFields.includes(contextKey)) {
      invalidFields.push(contextKey)
    }
    if (filterKeys && typeof filterKeys === 'string') {
      filterKeys.split(/\|/g).forEach(filterKey => {
        filterKey = filterKey.trim()
        const filter = filters[filterKey]
        if (filter) {
          substitution = filter(substitution)
        } else if (options.throwOnError && !invalidFilters.includes(filterKey)) {
          invalidFilters.push(filterKey)
        }
      })
    }
    return substitution
  })
  if (invalidFields.length || invalidFilters.length) {
    throw new RenderStringError({ invalidPlaceholders: invalidFields, invalidFilters })
  }
  return newValue
}
